// awsClients.js - Central management for AWS clients
import { getAuth } from './authUtils';

// Track the last time each client was requested to prevent rapid successive calls
const lastClientRequest = {
  bedrock: 0,
  dynamodb: 0,
  s3: 0
};

// Minimum time between client requests (in milliseconds)
const MIN_REQUEST_INTERVAL = 1000;

/**
 * Get an AWS Bedrock client with valid credentials
 * @param {Object} options Additional options for client initialization
 * @returns {Promise<BedrockRuntimeClient>} Configured Bedrock client
 */
export async function getBedrockClient(options = {}) {
  const now = Date.now();
  const caller = options.caller || 'unknown';
  
  // Only track time if not forcing a refresh
  if (!options.forceRefresh && now - lastClientRequest.bedrock < MIN_REQUEST_INTERVAL) {
    console.log(`[CLIENT] Bedrock client requested again too soon by ${caller}, reusing existing client`);
    const auth = getAuth({ trackingSource: `bedrock-reuse-${caller}` });
    return auth.getBedrockRuntimeClient();
  }
  
  lastClientRequest.bedrock = now;
  const auth = getAuth({ trackingSource: `bedrock-${caller}` });
  if (!auth) {
    throw new Error('Authentication context not available');
  }
  
  return await auth.getBedrockRuntimeClient();
}

/**
 * Get an AWS DynamoDB client with valid credentials
 * @param {Object} options Additional options for client initialization
 * @returns {Promise<DynamoDBClient>} Configured DynamoDB client
 */
export async function getDynamoDBClient(options = {}) {
  const now = Date.now();
  const caller = options.caller || 'unknown';
  
  // Only track time if not forcing a refresh
  if (!options.forceRefresh && now - lastClientRequest.dynamodb < MIN_REQUEST_INTERVAL) {
    console.log(`[CLIENT] DynamoDB client requested again too soon by ${caller}, reusing existing client`);
    const auth = getAuth({ trackingSource: `dynamodb-reuse-${caller}` });
    return auth.getDynamoDBClient();
  }
  
  lastClientRequest.dynamodb = now;
  const auth = getAuth({ trackingSource: `dynamodb-${caller}` });
  if (!auth) {
    throw new Error('Authentication context not available');
  }
  
  return await auth.getDynamoDBClient();
}

/**
 * Get an AWS S3 client with valid credentials
 * @param {Object} options Additional options for client initialization
 * @returns {Promise<S3Client>} Configured S3 client
 */
export async function getS3Client(options = {}) {
  const now = Date.now();
  const caller = options.caller || 'unknown';
  
  // Only track time if not forcing a refresh
  if (!options.forceRefresh && now - lastClientRequest.s3 < MIN_REQUEST_INTERVAL) {
    console.log(`[CLIENT] S3 client requested again too soon by ${caller}, reusing existing client`);
    const auth = getAuth({ trackingSource: `s3-reuse-${caller}` });
    return auth.getS3Client();
  }
  
  lastClientRequest.s3 = now;
  const auth = getAuth({ trackingSource: `s3-${caller}` });
  if (!auth) {
    throw new Error('Authentication context not available');
  }
  
  return await auth.getS3Client();
}

/**
 * Get all AWS clients needed for the application
 * @param {Object} options Additional options for clients initialization
 * @returns {Promise<Object>} Object containing all AWS clients
 */
export async function getAllAwsClients(options = {}) {
  const caller = options.caller || 'unknown';
  
  const [bedrockClient, dynamoDBClient, s3Client] = await Promise.all([
    getBedrockClient({ ...options, caller: `getAllClients-${caller}` }),
    getDynamoDBClient({ ...options, caller: `getAllClients-${caller}` }),
    getS3Client({ ...options, caller: `getAllClients-${caller}` })
  ]);
  
  return {
    bedrockClient,
    dynamoDBClient,
    s3Client
  };
}